import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Paper, Typography, Chip, Button } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface Props {
  file: File | null;
  onFile: (file: File | null) => void;
  disabled?: boolean;
}

const CsvDropzone: React.FC<Props> = ({ file, onFile, disabled }) => {
  const onDrop = useCallback((accepted: File[]) => {
    if (accepted.length > 0) onFile(accepted[0]);
  }, [onFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'], 'application/vnd.ms-excel': ['.csv'] },
    maxFiles: 1,
    maxSize: 2 * 1024 * 1024,
    disabled
  });

  return (
    <Paper
      variant="outlined"
      {...getRootProps()}
      sx={{
        p: 3, mt: 2, textAlign: 'center', cursor: disabled ? 'not-allowed' : 'pointer',
        bgcolor: isDragActive ? 'warning.lighter' : 'background.paper',
        border: '2px dashed',
        borderColor: file ? 'success.main' : (isDragActive ? 'warning.main' : 'divider')
      }}
    >
      <input {...getInputProps()} />
      {file ? (
        <Box>
          <CheckCircleIcon color="success" sx={{ fontSize: 48 }} />
          <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 600 }}>{file.name}</Typography>
          <Chip label={`${(file.size / 1024).toFixed(1)} KB`} size="small" sx={{ mt: 0.5 }} />
          <Box mt={1}>
            <Button size="small" onClick={(e) => { e.stopPropagation(); onFile(null); }} disabled={disabled}>
              Remove
            </Button>
          </Box>
        </Box>
      ) : (
        <Box>
          <UploadFileIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          <Typography variant="subtitle1" sx={{ mt: 1 }}>
            {isDragActive ? 'Drop the CSV here…' : 'Drag & drop a CSV file here, or click to browse'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Max 2 MB. Must match the bypass-template.csv schema.
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default CsvDropzone;
